import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, MapPin } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["Trusted, Compassionate", "Care — Right at Home"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-40 pb-16 sm:pt-36 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center min-h-[calc(100svh-8rem)]">
        {/* Text */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="h-px w-10 bg-sage" />
            <span className="eyebrow">In-Home Caregiver · {BUSINESS.area}</span>
          </motion.div>

          <h1 className="font-serif text-charcoal font-medium text-[3.1rem] leading-[0.98] sm:text-7xl lg:text-[5.5rem] tracking-tight">
            {HEADLINE.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-7 max-w-xl text-lg text-charcoal-soft leading-relaxed"
          >
            Certified Nursing Assistant with over 15 years of experience, serving
            families across Grand Junction & Mesa County with consistent,
            personal one-on-one care.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-button"
              className="group inline-flex items-center gap-3 rounded-full bg-sage text-white pl-6 pr-5 py-4 font-medium hover:bg-sage-dark transition-colors"
            >
              <Phone className="h-5 w-5" />
              Call Nicole Today
              <span className="rounded-full bg-white/20 px-3 py-1 text-sm group-hover:bg-white/30 transition-colors">
                {BUSINESS.phoneDisplay}
              </span>
            </a>
            <a
              href="#services"
              data-testid="hero-services-button"
              className="inline-flex items-center rounded-full border border-charcoal/20 px-7 py-4 text-charcoal hover:bg-white/60 transition-colors"
            >
              Explore Care Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex items-center gap-2 text-sm text-charcoal-soft"
          >
            <MapPin className="h-4 w-4 text-terracotta" />
            {BUSINESS.area}
          </motion.div>
        </div>

        {/* Image */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[2rem] soft-shadow-lg aspect-[4/5]">
              <motion.img
                src={IMAGES.heroHands}
                alt="Caring hands — compassionate in-home care"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/25 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-sand rounded-2xl soft-shadow border border-charcoal/5 px-5 py-4"
            >
              <p className="font-serif text-4xl text-sage-700 leading-none">15+</p>
              <p className="text-xs text-charcoal-soft mt-1 max-w-[8rem]">
                years caring for families like yours
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
